// src/components/registration/ForgotPasswordModal.tsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { requestPasswordReset } from "../../services/passwordService";
import "./Modal.css";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin?: () => void;
}

// How many seconds the user must wait before they can resend
const RESEND_COOLDOWN_SECONDS = 60;

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // "idle" | "sent" — once sent, show the success screen instead of the form
  const [state, setState] = useState<"idle" | "sent">("idle");
  // Countdown seconds remaining until resend is allowed (0 = allowed)
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start the resend cooldown timer
  const startCountdown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN_SECONDS);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Reset everything when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setFieldError("");
      setIsLoading(false);
      setState("idle");
      setCountdown(0);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }
  }, [isOpen]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const sendRequest = async (emailValue: string) => {
    setIsLoading(true);
    try {
      await requestPasswordReset(emailValue.trim());
    } catch {
      // Backend always returns 200 — any error here is a network issue.
      // Still show success to avoid leaking account existence.
    } finally {
      setIsLoading(false);
      setState("sent");
      startCountdown();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setFieldError(t("forgot_email_required"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldError(t("login_error_email_invalid"));
      return;
    }

    await sendRequest(email);
  };

  // Resend from the success screen — email is already validated
  const handleResend = async () => {
    if (countdown > 0 || isLoading) return;
    await sendRequest(email);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-container">
        <button
          className="modal-close-btn"
          onClick={onClose}
          aria-label={t("modal_close")}
        >
          ×
        </button>

        {state === "sent" ? (
          /* Success state */
          <>
            <div className="modal-header">
              <h2>{t("forgot_sent_title")}</h2>
              <p>{t("forgot_sent_subtitle")}</p>
            </div>
            <div className="modal-form">
              <div className="forgot-success-icon">✉️</div>
              <p className="forgot-success-text">{t("forgot_sent_hint")}</p>

              {/* Resend section */}
              <div className="forgot-resend">
                <span className="forgot-resend-label">
                  {t("forgot_no_email")}
                </span>
                <button
                  type="button"
                  className="forgot-resend-btn"
                  onClick={handleResend}
                  disabled={countdown > 0 || isLoading}
                >
                  {isLoading
                    ? t("forgot_sending")
                    : countdown > 0
                    ? t("forgot_resend_countdown", { seconds: countdown })
                    : t("forgot_resend")}
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <p>
                {t("forgot_remember_password")}{" "}
                <button type="button" className="link-btn" onClick={onSwitchToLogin}>
                  {t("login_title")}
                </button>
              </p>
            </div>
          </>
        ) : (
          /* Default state — email input form */
          <>
            <div className="modal-header">
              <h2>{t("forgot_title")}</h2>
              <p>{t("forgot_subtitle")}</p>
            </div>

            <form onSubmit={handleSubmit} className="modal-form" noValidate>
              <div className="form-group">
                <label htmlFor="fp-email">{t("forgot_email_label")}</label>
                <input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setFieldError("");
                  }}
                  className={fieldError ? "input-error" : ""}
                  placeholder={t("forgot_email_placeholder")}
                  autoFocus
                  disabled={isLoading}
                />
                {fieldError && (
                  <span className="field-error">{fieldError}</span>
                )}
              </div>

              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading ? t("forgot_sending") : t("forgot_submit")}
              </button>
            </form>

            <div className="modal-footer">
              <p>
                {t("forgot_remember_password")}{" "}
                <button
                  type="button"
                  className="link-btn"
                  onClick={onSwitchToLogin}
                  disabled={isLoading}
                >
                  {t("login_title")}
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};